import { For, Match, Show, Switch, createMemo, createSignal } from "solid-js"
import { useDataSourceEditor } from "./st-data-source-editor"
import style from './st-data-source-editor.module.css'
import { createStore } from "solid-js/store"
import ButtonGroup from "../st-button-group/st-button-group"
import STButton from "../st-button/st-button"
import STInput from "../st-input/st-input"

export default function ParameterEditor() {
    const ctx = useDataSourceEditor()

    const [parameters, setParameters] = createStore<Record<string, Parameter>>({
        '@SQL_PARAM': new SQLParameter(),
        '@DATA_SOURCE_PARAM': new DataSourceParameter(),
        '@LOOKUP_PARAM': new LookupParameter(),
        '@DATE_PARAM': new DateParameter(),
        '@NUMBER_PARAM': new NumberParameter(),
        '@TEXT_PARAM': new TextParameter(),
    })

    const parameterNames = createMemo(() => Object.keys(parameters))

    const [selectedParameterName, setSelectedParameterName] = createSignal('@SQL_PARAM')

    const selectedParameter = createMemo(() => parameters[selectedParameterName()])

    const changeParameterType = (type: ParameterType) => {
        if (!selectedParameter()) return;
        if (selectedParameter().type === type) return;

        let param: Parameter;
        switch (type) {
            case 'SQL': param = new SQLParameter(); break;
            case 'Data Source': param = new DataSourceParameter(); break;
            case 'Lookup': param = new LookupParameter(); break;
            case 'Date': param = new DateParameter(); break;
            case 'Number': param = new NumberParameter(); break;
            case 'Text': param = new TextParameter(); break;
        }

        setParameters(selectedParameterName(), param);
    }

    return (
        <div class='flexbox' style='gap: 8px'>
            <b style='font-size:13px'>Parameters</b>
            <div class={style['parameter-editor-container']}>
                <div class={style['parameter-list']}>
                    <For each={parameterNames()}>{(paramName) => (
                        <button class={style['parameter-list-item']} data-selected={selectedParameterName() === paramName} onClick={() => setSelectedParameterName(paramName)}>{paramName}</button>
                    )}</For>
                </div>
                <div class={style['parameter-editor']}>
                    <Show when={selectedParameter()} fallback={<NoParamSelected/>}>
                        <ButtonGroup>
                            <STButton icon='fa fa-database' onClick={() => changeParameterType('SQL')} active={selectedParameter()?.type === 'SQL'}>
                                SQL
                            </STButton>
                            <STButton icon='fa fa-folder' onClick={() => changeParameterType('Data Source')} active={selectedParameter()?.type === 'Data Source'}>
                                <span style='white-space: nowrap'>
                                    Data Source
                                </span>
                            </STButton>
                            <STButton icon='fa fa-table' onClick={() => changeParameterType('Lookup')} active={selectedParameter()?.type === 'Lookup'}>
                                Lookup
                            </STButton>
                            <STButton icon='fa fa-calendar' onClick={() => changeParameterType('Date')} active={selectedParameter()?.type === 'Date'}>
                                Date
                            </STButton>
                            <STButton icon='fa fa-dollar-sign' onClick={() => changeParameterType('Number')} active={selectedParameter()?.type === 'Number'}>
                                Number
                            </STButton>
                            <STButton icon='fa fa-pencil' onClick={() => changeParameterType('Text')} active={selectedParameter()?.type === 'Text'}>
                                Text
                            </STButton>
                        </ButtonGroup>
                    </Show>

                    <Switch>
                        <Match when={selectedParameter()?.type === 'SQL'}>
                            <SQLParameterEditor />
                        </Match>
                    </Switch>
                </div>
            </div>
        </div>
    )
}

const NoParamSelected = () => {
    return (
        <div>No parameter selected</div>
    )
}

const SQLParameterEditor = () => {
    const displaySequence = createSignal('0')
    const inputLabelOverride = createSignal('')
    const value = createSignal('')
    const saveCurrentValueAsDefault = createSignal(false)

    return (
        <div class='flexbox'>
            <STInput type='number' value={displaySequence} label='Display Sequence'></STInput>
        </div>
    )
}



export type ParameterType = 'SQL' | 'Data Source' | 'Lookup' | 'Date' | 'Number' | 'Text'

export abstract class Parameter {
    abstract readonly type: ParameterType
    name: string = ''
    displaySequence: number = 0
    inputLabelOverride: string = ''
}

export class SQLParameter extends Parameter {
    type = 'SQL' as ParameterType
    query: string = ''
    useCurrentParamAsDefault: boolean = false
    useFirstListValueAsDefault: boolean = true
    currentParamValue: string | undefined
    displayField: string | undefined
    valueField: string | undefined
}

export class DataSourceParameter extends Parameter {
    type = 'Data Source' as ParameterType
    currentParamValue: string | undefined
    useCurrentParamAsDefault: boolean = false
    useFirstListValueAsDefault: boolean = true
    dataSource: string | undefined
    displayField: string | undefined
    valueField: string | undefined
}

export class LookupParameter extends Parameter {
    type = 'Lookup' as ParameterType
    currentParamValue: string | undefined
    useCurrentParamAsDefault: boolean = false
    useFirstListValueAsDefault: boolean = true
    lookupType: unknown //TODO: Not sure what the valid options are
    //TODO: Potentially able to define a custom static lookup here
}

export class DateParameter extends Parameter {
    type = 'Date' as ParameterType
    offset: number = 0
    date: string = ''
    useCurrentParamAsDefault: boolean = false
}

export class NumberParameter extends Parameter {
    type = 'Number' as ParameterType
    number: number = 0
    useCurrentParamAsDefault: boolean = false
}

export class TextParameter extends Parameter {
    type = 'Text' as ParameterType
    text: string = ''
    useCurrentParamAsDefault: boolean = false
}
